import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { CheckCircle2, Plus } from 'lucide-react';

import QualificationDialog from './QualificationDialog';
import QualificationDetails from './QualificationDetails/QualificationDetails';
import { useEntryDialog } from '../useCareerEntries';

import useAuth from 'hooks/useAuth';
import { getQualificationEntries } from 'helpers/portfolioLayout';

import styles from '../CareerEntries.module.scss';
import hocStyles from '../../hoc/hoc.module.scss';
import Loader from 'components/Loader/Loader';

const {
  panel,
  dashedPanel,
  headerContainer,
  header,
  count,
  hiddenBadge,
  placeholder: placeholderStyle,
  addLink,
  addBtn,
  editWrp,
  loader,
  tick,
} = hocStyles;

// Rows shown before "Show all N".
const VISIBLE_ROWS = 4;

const META = {
  lastUpdated: 'qualifications',
  eventName: 'freelancer-updates-qualifications',
};

// `editor` is useCareerEntries for user.education, shared with Education.
const Qualification = ({
  editor,
  isOwn,
  user,
  isEmpty,
  placeholder,
  addRequest,
  onAddEducation,
}) => {
  const { lastUpdated, updating, updated } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const { dialog, open, close } = useEntryDialog(addRequest);
  const { visibleEntries, saving, saveEntry, deleteEntry } = editor;
  const qualifications = getQualificationEntries(visibleEntries);

  const instanceIsUpdating = lastUpdated === 'qualifications';

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !dialog.open && (
      <CheckCircle2 className={tick} size={20} aria-label="Saved" />
    );

  if (!isOwn && isEmpty) {
    return null;
  }

  const showOwnerEmpty = isOwn && qualifications.length === 0;
  const total = qualifications.length;
  const visible = showAll
    ? qualifications
    : qualifications.slice(0, VISIBLE_ROWS);

  return (
    <section
      id="portfolio-qualification"
      className={showOwnerEmpty ? dashedPanel : panel}
    >
      <div className={headerContainer}>
        <h2 className={header}>
          Certifications and awards
          {total > VISIBLE_ROWS && <span className={count}>· {total}</span>}
        </h2>
        {showOwnerEmpty ? (
          <span className={hiddenBadge}>Hidden until added</span>
        ) : (
          user &&
          isOwn && (
            <div className={editWrp}>
              {successTick}
              {instanceIsUpdating && updating && !dialog.open && (
                <Loader loading className={loader} />
              )}
              <button
                id="add-qualification"
                type="button"
                className={addBtn}
                onClick={() => open()}
                aria-label="Add a certification or award"
              >
                <Plus size={14} strokeWidth={2.4} aria-hidden="true" />
                Add
              </button>
            </div>
          )
        )}
      </div>
      {showOwnerEmpty ? (
        <p className={placeholderStyle}>
          {placeholder}{' '}
          <button type="button" className={addLink} onClick={() => open()}>
            Add a certification or award
          </button>
        </p>
      ) : (
        <div className={styles.compactList}>
          {visible.map((qualification, index) => (
            <QualificationDetails
              key={
                qualification.id
                  ? `quDetails${qualification.id}`
                  : `quDetails${index}`
              }
              qualification={qualification}
              isOwn={isOwn}
              user={user}
              onEdit={() => open(qualification)}
            />
          ))}
          {total > VISIBLE_ROWS && (
            <button
              type="button"
              className={styles.showAll}
              onClick={() => setShowAll(!showAll)}
              aria-expanded={showAll}
            >
              {showAll ? 'Show fewer' : `Show all ${total}`}
            </button>
          )}
        </div>
      )}

      {isOwn && dialog.key > 0 && (
        <QualificationDialog
          key={dialog.key}
          show={dialog.open}
          qualification={dialog.entry}
          saving={saving}
          onSave={(qualification) =>
            saveEntry(dialog.entry, qualification, META)
          }
          onDelete={() => {
            close();
            deleteEntry(
              dialog.entry,
              `Deleted: ${dialog.entry.qualification}`,
              META
            );
          }}
          onClose={close}
          onAddEducation={onAddEducation}
        />
      )}
    </section>
  );
};

Qualification.propTypes = {
  user: PropTypes.object,
  isOwn: PropTypes.bool,
  editor: PropTypes.shape({
    visibleEntries: PropTypes.array.isRequired,
    saving: PropTypes.bool,
    saveEntry: PropTypes.func.isRequired,
    deleteEntry: PropTypes.func.isRequired,
  }).isRequired,
  isEmpty: PropTypes.bool,
  placeholder: PropTypes.string,
  addRequest: PropTypes.number,
  onAddEducation: PropTypes.func,
};

export default Qualification;
